import {
  IWuAppHeaderMenuItem,
  IWuAppHeaderSubMenuItem,
} from '@/components/appHeader/types/WuAppHeaderMenuItem'
import {cn} from '@/lib/utils'
import React, {useMemo, useState} from 'react'
import {ProductItem} from './_productItem'

interface IProductListProps {
  category: IWuAppHeaderMenuItem
  currentProduct: string
  activeProductName: string
  dir?: 'ltr' | 'rtl'
  focusItem: string
  setFocusItem: (value: string) => void
  borderColorLocked?: string
}

export const ProductList: React.FC<IProductListProps> = ({
  category,
  currentProduct,
  activeProductName,
  dir = 'ltr',
  focusItem,
  setFocusItem,
  borderColorLocked = '#f5a300',
}) => {
  const {products = [], active} = category

  const sortedActiveProducts = useMemo(
    () =>
      products
        .filter(item => item.active)
        .sort((a, b) => (a.order || 100) - (b.order || 100)),
    [products],
  )

  const inactiveProducts = useMemo(
    () => products.filter(item => !item.active),
    [products],
  )

  const [lockedBg, setLockedBg] = useState(borderColorLocked)

  const renderProductItems = (
    items: IWuAppHeaderSubMenuItem[],
  ): React.ReactNode =>
    items.map((item, i) => (
      <ProductItem
        key={i}
        product={item}
        currentProduct={currentProduct}
        activeProductName={activeProductName}
        dir={dir}
        focusItem={focusItem}
        setFocusItem={setFocusItem}
      />
    ))

  if (!active) {
    return (
      <li
        className={cn(
          'productListItem learnMore focus:outline-none focus-visible:outline-none wu-product-item-inactive !bg-[#f5f5f5] hover:!bg-[#eeeeee]',
          focusItem === 'learn' && 'activeProduct',
        )}
        tabIndex={0}
        onFocus={() => setFocusItem('learn')}
        onBlur={() => setFocusItem('')}
        aria-label="Learn More"
      >
        <div
          className={cn('topBorder', dir === 'rtl' && 'topBorderRtl')}
          style={{backgroundColor: borderColorLocked}}
        />
        <a
          className="productLink"
          href="https://www.questionpro.com/a/showUpgradeUser.do"
          tabIndex={-1}
        >
          <span className="learnMoreText">Learn More</span>
        </a>
      </li>
    )
  }

  return (
    <>
      {renderProductItems(sortedActiveProducts)}

      {inactiveProducts.length > 0 && (
        <li
          className="productListItem focus:outline-none focus-visible:outline-none !bg-[#f5f5f5] hover:!bg-[#eeeeee] group"
          onMouseOver={() => setLockedBg('#f5a300')}
          onMouseOut={() => setLockedBg(borderColorLocked)}
          aria-label="Upgrade to unlock"
        >
          <div
            className={cn('topBorder', dir === 'rtl' && 'topBorderRtl')}
            style={{backgroundColor: lockedBg}}
          />
          <a
            href="https://www.questionpro.com/a/showUpgradeUser.do"
            className="productLink flex flex-col items-center justify-center py-2 min-w-[88px]"
          >
            <span className="text-base transition-colors wm-lock text-orange-soft group-hover:text-orange-deep"></span>
            <span className="text-[10px] leading-[10px] text-center pt-1 !text-orange-deep">
              Upgrade to <br /> unlock
            </span>
          </a>
        </li>
      )}

      {renderProductItems(inactiveProducts)}
    </>
  )
}
